import type {
	BatchItem,
	BatchResponse,
	Click,
	Link,
	LinkSortKey,
	LinkStats,
	SortDir,
} from "shared";
import { api } from "./client";

export interface ListLinksResult {
	links: Link[];
	nextCursor: string | null;
	stats: LinkStats;
}

export interface CreateLinkInput {
	targetUrl: string;
	hash?: string;
	/** false = client-suggested auto hash (not user-typed vanity). */
	isCustom?: boolean;
	startsAt?: string | null;
	expiresAt?: string | null;
}

export interface CreateLinkResult {
	link: Link;
	reassigned: boolean;
}

export interface UpdateLinkInput {
	targetUrl?: string;
	active?: boolean;
	startsAt?: string | null;
	expiresAt?: string | null;
	hash?: string;
	/** Manual archive/unarchive (organizational; does not gate redirect). */
	archived?: boolean;
}

export type ArchivedFilter = "false" | "true" | "all";

export async function listLinks(opts?: {
	q?: string;
	cursor?: string;
	limit?: number;
	archived?: ArchivedFilter;
	sort?: LinkSortKey;
	order?: SortDir;
}): Promise<ListLinksResult> {
	const params = new URLSearchParams();
	if (opts?.q) params.set("q", opts.q);
	if (opts?.cursor) params.set("cursor", opts.cursor);
	if (opts?.limit) params.set("limit", String(opts.limit));
	if (opts?.archived) params.set("archived", opts.archived);
	if (opts?.sort) params.set("sort", opts.sort);
	if (opts?.order) params.set("order", opts.order);
	const qs = params.toString();
	return api.get(`/links${qs ? `?${qs}` : ""}`);
}

export async function createLink(
	input: CreateLinkInput,
): Promise<CreateLinkResult> {
	return api.post("/links", input);
}

export async function importLinks(items: BatchItem[]): Promise<BatchResponse> {
	return api.post("/links/batch", { items });
}

export async function getLink(id: string): Promise<{ link: Link }> {
	return api.get(`/links/${id}`);
}

export async function updateLink(
	id: string,
	input: UpdateLinkInput,
): Promise<{ link: Link }> {
	return api.patch(`/links/${id}`, input);
}

export async function deleteLink(id: string): Promise<void> {
	await api.del(`/links/${id}`);
}

export async function listClicks(
	id: string,
	opts?: { cursor?: string; limit?: number },
): Promise<{ clicks: Click[]; nextCursor: string | null }> {
	const params = new URLSearchParams();
	if (opts?.cursor) params.set("cursor", opts.cursor);
	if (opts?.limit) params.set("limit", String(opts.limit));
	const qs = params.toString();
	return api.get(`/links/${id}/clicks${qs ? `?${qs}` : ""}`);
}
